import type { AdminUserDetail } from '@steado/admin-contract/user-detail';

import { ChipsCell } from '@/components/table/cells';

import { EntitlementPanel } from './EntitlementPanel';
import { GymPanel } from './GymPanel';
import { HealthPanel } from './HealthPanel';
import { ReturnDecisionsPanel } from './ReturnDecisionsPanel';
import { FieldRow, FieldRows, Panel } from '@/components/panel/panels';
import { OwnerBadge } from '@/components/panel/OwnerBadge';
import { formatDate, humanise } from '@/lib/format';

interface ProfileTabProps {
  readonly detail: AdminUserDetail;
}

/**
 * Everything about one person that is not an event.
 *
 * Two columns of cards rather than one tall stack, and each card says who owns
 * what is in it. The owner badge is not decoration: a support operator reading
 * "Targets" needs to know it is derived arithmetic before they go looking for
 * the form that sets it, and there is none. Only three of these cards can be
 * written from this console at all.
 */
export function ProfileTab({ detail }: ProfileTabProps) {
  return (
    <div className="grid grid-cols-1 items-start gap-3 xl:grid-cols-2">
      <div className="flex flex-col gap-3">
        <Panel title="Account" owner={<OwnerBadge>Theirs, from sign-up</OwnerBadge>} bleed>
          <FieldRows>
            <FieldRow label="Phone" value={detail.phone} mono />
            <FieldRow label="Status" value={detail.status} />
            <FieldRow label="Entitlement" value={detail.entitlement} />
            <FieldRow label="Timezone" value={detail.timezone} />
            <FieldRow label="Locale" value={detail.locale} />
            <FieldRow label="Joined" value={formatDate(detail.createdAt)} />
            <FieldRow
              label="Onboarded"
              value={detail.onboardedAt === null ? null : formatDate(detail.onboardedAt)}
            />
            <FieldRow
              label="Deactivated"
              value={detail.deactivatedAt === null ? null : formatDate(detail.deactivatedAt)}
              {...(detail.deactivatedAt === null ? {} : ({ tone: 'err' } as const))}
            />
          </FieldRows>
        </Panel>

        <GymPanel detail={detail} />

        <Panel title="Body and goals" owner={<OwnerBadge>They answered this</OwnerBadge>} bleed>
          {detail.profile === null ? (
            <p className="border-t border-border px-[18px] py-3.5 text-[13px] text-muted-foreground">
              Onboarding is unfinished, so there is no profile to compute targets from.
            </p>
          ) : (
            <FieldRows>
              <FieldRow label="Sex" value={detail.profile.sex} />
              <FieldRow label="Age" value={`${String(detail.profile.ageYears)} years`} />
              <FieldRow label="Height" value={`${String(detail.profile.heightCm)} cm`} mono />
              <FieldRow label="Weight" value={`${String(detail.profile.weightKg)} kg`} mono />
              <FieldRow label="Target weight" value={`${String(detail.profile.targetWeightKg)} kg`} mono />
              <FieldRow label="Deadline" value={humanise(detail.profile.targetDeadline)} />
              <FieldRow label="Activity" value={humanise(detail.profile.activityLevel)} />
              <FieldRow label="Goals" value={<ChipsCell items={detail.goals.map(humanise)} max={4} />} />
            </FieldRows>
          )}
        </Panel>

        <Panel title="Diet" owner={<OwnerBadge>They answered this</OwnerBadge>} bleed>
          {detail.diet === null ? (
            <p className="border-t border-border px-[18px] py-3.5 text-[13px] text-muted-foreground">
              No diet preferences recorded.
            </p>
          ) : (
            <FieldRows>
              <FieldRow label="Diet" value={humanise(detail.diet.type)} />
              <FieldRow
                label="Cuisines"
                value={<ChipsCell items={detail.diet.cuisines.map(humanise)} max={4} />}
              />
              <FieldRow label="Veg-only days" value={<ChipsCell items={detail.diet.vegOnlyDays} max={7} />} />
            </FieldRows>
          )}
        </Panel>

        <Panel title="Training preferences" owner={<OwnerBadge>They answered this</OwnerBadge>} bleed>
          {detail.workout === null ? (
            <p className="border-t border-border px-[18px] py-3.5 text-[13px] text-muted-foreground">
              No training preferences recorded.
            </p>
          ) : (
            <FieldRows>
              <FieldRow label="Training days" value={detail.workout.trainingDays.map(humanise).join(', ')} />
              <FieldRow label="Session length" value={`${String(detail.workout.sessionMinutes)} min`} />
              <FieldRow label="Preferred time" value={humanise(detail.workout.preferredTime)} />
              <FieldRow label="Experience" value={detail.workout.experienceLevel} />
              <FieldRow
                label="Locations"
                value={<ChipsCell items={detail.workout.locations.map(humanise)} max={4} />}
              />
              <FieldRow
                label="Injury flags"
                value={<ChipsCell items={detail.workout.injuryFlags.map(humanise)} max={4} />}
              />
            </FieldRows>
          )}
        </Panel>
      </div>

      <div className="flex flex-col gap-3">
        <Panel title="Targets" owner={<OwnerBadge>Derived, not set</OwnerBadge>} bleed>
          {detail.targets === null ? (
            <p className="border-t border-border px-[18px] py-3.5 text-[13px] text-muted-foreground">
              No targets. They are derived from the profile, and there is none yet. Nothing is fabricated in
              their place.
            </p>
          ) : (
            <FieldRows>
              <FieldRow label="Calories" value={`${String(detail.targets.kcal)} kcal`} mono />
              <FieldRow label="Protein" value={`${String(detail.targets.proteinG)} g`} mono />
              <FieldRow label="Carbs" value={`${String(detail.targets.carbsG)} g`} mono />
              <FieldRow label="Fat" value={`${String(detail.targets.fatG)} g`} mono />
            </FieldRows>
          )}
        </Panel>

        {/*
          Only shown when it differs. For a user with no supplements it is the
          targets panel again, and a second identical panel reads as a bug; for
          a mass gainer user it is most of a meal lower, and an operator
          comparing the plan against "Targets" would otherwise conclude the plan
          was wrong.
        */}
        {detail.foodBudget !== null &&
          detail.targets !== null &&
          detail.foodBudget.kcal !== detail.targets.kcal && (
            <Panel
              title="From food"
              description="What the meal plan is built to: the targets above minus the supplement stack."
              owner={<OwnerBadge>Derived, not set</OwnerBadge>}
              bleed
            >
              <FieldRows>
                <FieldRow label="Calories" value={`${String(detail.foodBudget.kcal)} kcal`} mono />
                <FieldRow label="Protein" value={`${String(detail.foodBudget.proteinG)} g`} mono />
                <FieldRow label="Carbs" value={`${String(detail.foodBudget.carbsG)} g`} mono />
                <FieldRow label="Fat" value={`${String(detail.foodBudget.fatG)} g`} mono />
              </FieldRows>
            </Panel>
          )}

        <Panel title="Supplements" owner={<OwnerBadge>They answered this</OwnerBadge>} bleed>
          {detail.supplements.length === 0 ? (
            <p className="border-t border-border px-[18px] py-3.5 text-[13px] text-muted-foreground">None.</p>
          ) : (
            <ul>
              {detail.supplements.map((supplement) => (
                <li
                  key={supplement.id}
                  className="flex items-baseline gap-3 border-t border-border px-[18px] py-2.5"
                >
                  <span className="min-w-0 text-[13px] text-foreground">
                    {supplement.name}
                    {supplement.timing.length > 0 && (
                      <span className="ml-2 text-xs text-muted-foreground">
                        {supplement.timing.map((slot) => slot.replace(/_/g, ' ')).join(', ')}
                      </span>
                    )}
                  </span>
                  <span className="ml-auto shrink-0 text-right font-mono text-xs text-muted-foreground">
                    {String(supplement.servingsPerDay)}×{String(supplement.doseAmount)}
                    {supplement.doseUnit === 'g' || supplement.doseUnit === 'ml'
                      ? supplement.doseUnit
                      : ` ${supplement.doseUnit}`}{' '}
                    · {String(supplement.kcalPerServing * supplement.servingsPerDay)} kcal,{' '}
                    {String(supplement.proteinPerServingG * supplement.servingsPerDay)}g protein
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <HealthPanel userId={detail.id} redaction={detail.health} />

        <EntitlementPanel userId={detail.id} subscription={detail.subscription} />

        <ReturnDecisionsPanel
          userId={detail.id}
          injuryFlags={detail.workout?.injuryFlags ?? []}
          decisions={detail.returnDecisions}
        />
      </div>
    </div>
  );
}
